<template>
  <PageHeader eyebrow="Community" title="High Scores" description="Top published runs, sorted by score." />
  <div class="highscores-container">
    <div v-if="loading" class="hs-loading">Loading…</div>

    <div v-else-if="entries.length === 0" class="hs-empty">
      <p>No scores published yet. Complete a run and publish it from your account page.</p>
    </div>

    <div v-else class="runs-table-wrap">
      <table class="runs-table">
        <thead>
          <tr>
            <th @click="setSort('username')" class="sortable">
              Player <i :class="sortIcon('username')"></i>
            </th>
            <th @click="setSort('completedAt')" class="sortable col-hide-mobile">
              Date <i :class="sortIcon('completedAt')"></i>
            </th>
            <th @click="setSort('complexity')" class="sortable col-hide-mobile">
              Complexity <i :class="sortIcon('complexity')"></i>
            </th>
            <th @click="setSort('challengeAmount')" class="sortable col-hide-mobile">
              Challenges <i :class="sortIcon('challengeAmount')"></i>
            </th>
            <th @click="setSort('score')" class="sortable">
              Score <i :class="sortIcon('score')"></i>
            </th>
            <th @click="setSort('correctPct')" class="sortable col-hide-mobile">
              Correct <i :class="sortIcon('correctPct')"></i>
            </th>
            <th @click="setSort('timePerChallenge')" class="sortable col-hide-mobile">
              Sec / challenge <i :class="sortIcon('timePerChallenge')"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in sortedEntries" :key="entry.id">
            <td class="player-name">{{ entry.username ?? '—' }}</td>
            <td class="col-hide-mobile">{{ formatDate(entry.completedAt) }}</td>
            <td class="col-hide-mobile">{{ entry.complexity }}</td>
            <td class="col-hide-mobile">{{ entry.challengeAmount }}</td>
            <td>{{ entry.score }}</td>
            <td class="col-hide-mobile" :class="scoreClass(entry.correctPct ?? entry.score)">
              {{ entry.correctPct != null ? entry.correctPct + '%' : '—' }}
            </td>
            <td class="col-hide-mobile">{{ entry.timePerChallenge }}s</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useAuth} from '@/composables/useAuth';
import PageHeader from '@/components/parts/PageHeader.vue';

const {fetchLeaderboard} = useAuth();

const entries = ref([]);
const loading = ref(false);
const sortKey = ref('score');
const sortDir = ref('desc');

onMounted(async () => {
  loading.value = true;
  try {
    entries.value = await fetchLeaderboard();
  } catch (err) {
    console.error('Failed to fetch leaderboard:', err);
  } finally {
    loading.value = false;
  }
});

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'score' ? 'desc' : 'asc';
  }
}

function sortIcon(key) {
  if (sortKey.value !== key) return 'ri-expand-up-down-line sort-icon sort-icon--inactive';
  return sortDir.value === 'asc' ? 'ri-arrow-up-s-line sort-icon' : 'ri-arrow-down-s-line sort-icon';
}

const sortedEntries = computed(() => {
  const dir = sortDir.value === 'asc' ? 1 : -1;
  return [...entries.value].sort((a, b) => {
    let av = a[sortKey.value];
    let bv = b[sortKey.value];
    if (av == null) return 1;
    if (bv == null) return -1;
    // Firestore Timestamps: compare via toMillis
    if (av?.toMillis) av = av.toMillis();
    if (bv?.toMillis) bv = bv.toMillis();
    if (typeof av === 'string') return av.localeCompare(bv) * dir;
    return (av - bv) * dir;
  });
});

function formatDate(ts) {
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function scoreClass(score) {
  if (score >= 80) return 'score-good';
  if (score >= 50) return 'score-mid';
  return 'score-low';
}
</script>

<style scoped>
.highscores-container {
  width: 100%;
}

.hs-loading,
.hs-empty {
  text-align: left;
  color: var(--text-color);
  padding: 20px 0;
}

.runs-table-wrap {
  overflow-x: auto;
}

.runs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: var(--text-color-bright);
}

.runs-table th,
.runs-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid var(--highlighted);
}

.runs-table th {
  color: var(--text-color);
  font-weight: normal;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.05em;
}

.runs-table th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.runs-table th.sortable:hover {
  color: var(--text-color-bright);
}

.sort-icon {
  font-size: 12px;
  vertical-align: middle;
}

.sort-icon--inactive {
  opacity: 0.3;
}

.player-name {
  color: var(--text-color-bright);
}

.score-good { color: #4caf50; }
.score-mid  { color: var(--geogaddi-y); }
.score-low  { color: var(--geogaddi-r); }

@media (max-width: 639px) {
  .runs-table th,
  .runs-table td {
    padding: 8px 6px;
  }
}
</style>
